import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { AnimatedDialog, Button, Spinner, VirtualTable } from "@/components/reusable";
import type { ParsedWorksheet } from "@/types";

interface SchemaFilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  worksheets: ParsedWorksheet[];
  onConfirm: () => void;
  onCancel: () => void;
  isUploading?: boolean;
}

export function SchemaFilePreviewDialog({
  open,
  onOpenChange,
  fileName,
  worksheets,
  onConfirm,
  onCancel,
  isUploading = false,
}: SchemaFilePreviewDialogProps) {
  const uploadedAt = new Date().toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <AnimatedDialog
      open={open}
      onOpenChange={isUploading ? () => {} : onOpenChange}
      maxWidth="max-w-4xl"
      className="p-6 max-h-[85vh] overflow-hidden flex flex-col"
    >
      <div className="flex items-baseline justify-between mb-3">
        <Dialog.Title className="text-lg font-semibold text-[var(--color-text-primary)]">
          Preview of Schema Source File
          <span className="ml-2 text-sm font-normal text-[var(--color-text-tertiary)]">
            – {fileName}
          </span>
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          Preview your file columns before building a schema
        </Dialog.Description>
        <span className="shrink-0 text-xs text-[var(--color-text-tertiary)]">
          uploaded {uploadedAt}
        </span>
      </div>

      <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
        Your columns will be mapped to this schema in the following step.
      </p>

      <div className="min-h-0 flex-1 overflow-hidden">
        {worksheets.length > 1 ? (
          <Tabs.Root defaultValue={worksheets[0]?.name}>
            <Tabs.List className="flex gap-1 border-b border-[var(--color-border-light)] mb-3">
              {worksheets.map((ws) => (
                <Tabs.Trigger
                  key={ws.name}
                  value={ws.name}
                  className="px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors border-b-2 data-[state=active]:border-[var(--color-brand-primary)] data-[state=active]:text-[var(--color-brand-primary)] data-[state=inactive]:border-transparent data-[state=inactive]:text-[var(--color-text-tertiary)] data-[state=inactive]:hover:text-[var(--color-text-secondary)]"
                >
                  {ws.name}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {worksheets.map((ws) => (
              <Tabs.Content key={ws.name} value={ws.name}>
                <VirtualTable
                  headers={ws.headers}
                  rows={ws.rows}
                  totalRows={ws.totalRows}
                  maxHeight="calc(85vh - 300px)"
                />
              </Tabs.Content>
            ))}
          </Tabs.Root>
        ) : worksheets[0] ? (
          <VirtualTable
            headers={worksheets[0].headers}
            rows={worksheets[0].rows}
            totalRows={worksheets[0].totalRows}
            maxHeight="calc(85vh - 260px)"
          />
        ) : null}
      </div>

      <div className="mt-5 shrink-0 text-center space-y-3">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          Does this look correct to you before continuing?
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isUploading}>
            Cancel Upload
          </Button>
          <Button onClick={onConfirm} disabled={isUploading}>
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Uploading...
              </span>
            ) : (
              "Confirm Upload"
            )}
          </Button>
        </div>
      </div>
    </AnimatedDialog>
  );
}
